import { Injectable } from '@angular/core';
import {
  AnimationBatch,
  AnimationInnerKey,
  AnimationItem,
  AnimationKeys,
  GeneralShapeDescriptor,
  Translate,
  PrimitiveValue,
  Rotation,
  SVGAttributes,
  Scale,
  ShapeIRI,
  ShapePosition,
  ShapeTransform,
  ShowHideAnimation,
  StrokeConfig,
  _Scale,
  __Animation,
  NewTextAnimation,
  NewAnimationId,
} from '../../elements/resource/types/shape.type';
import {
  MainAnimationFrameObject,
  RootAnimationFrame,
} from './frame/main-animation-frame-object';
import { get as _get, set as _set, words } from 'lodash';
import {
  AnimationFrame,
  AutoAnimationFrame,
  BaseAnimationFrame,
  CanvasTransformFrame,
  SoundAnimationFrame,
  SubSceneTransitionFrame,
  TextAnimationFrame,
} from './components/animation-frame/animation.types';
import { GeneralShape } from '../shape/shapes/general/general-shape';
import { Store } from '@ngrx/store';
import {
  AnimationAction,
  AnimationId,
  ConsequtiveActionByKey,
} from '../../services/animation/animation.types';

import {
  AnimationsByFrame,
  AnimationsByShape,
} from './store/animation.reducer';
import {
  currentShapeTransforms,
  originalDescriptors,
  baseShapeTransforms,
  baseSvgAttributes,
} from '../store/selector/editor.selector';
import {
  AnimationFrameObject,
  AnimationFrameRelationships,
} from './frame/animation-frame-object';
import { FunctionAnimationFrameObject } from './frame/function/function-animation-frame';
import { SoundAnimationFrameObject } from './frame/sound-animation-frame';
import {
  getCurrentColorPalette,
  getCurrentLanguage,
  getCurrentProject,
} from '../../projects/project.selector';
import { SubSceneTransitionFrameObject } from './frame/subscene-transition.frame';
import {
  _screenX,
  _screenY,
  CanvasService,
} from '../../services/canvas/canvas.service';
import { Container, Sprite, Texture, Ticker } from 'pixi.js';
import { InverseAnimationFrameObject } from './frame/inverse/inverse-animation-frame.object';
import { SceneTransitionFrameObject } from './frame/scene-transition.frame';
import { TextAnimationFrameObject } from './frame/text-animation-frame';
import { AudioService } from './audio.service';
import { LocalSoundAnimationFrameObject } from './frame/local-sound-animation-frame';
import { Library, Project } from '../../projects/project.interface';
import { Observable } from 'rxjs';
import { CanvasTransformFrameObject } from './frame/canvas-transform-frame';
import { setCurrentAnimationTime } from './store/animation.actions';
import { AutoAnimationFrameObject } from './frame/auto-animation-frame-object';
import { BaseAnimationFrameObject } from './frame/base-animation-frame';
import { FileRootShape } from '../shape/shapes/general/root/file-root-shape';
import { BackgroundService } from '../store/studio/background/background.service';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { TextWord } from '../shape/shapes/text/text-word';
import {
  ImageAnimationDescriptor,
  ImageAnimationId,
} from '../shape/shapes/base/image-shape';
import { TextAnimationService } from './text-animation.service';
export interface AnimationParams {
  base: 'translate' | 'zoom';
  type: 'fade' | 'fade-mask';
  word?: 'fade' | 'translate' | 'zoom';
  segment?: boolean;
}

export type ExactValue = {
  type: 'exact';
  value: any;
};

export type OriginalValue = {
  type: 'original';
};

export type TransitionValue = {
  type: 'transition';
  from: ExactValue | OriginalValue;
  to: any;
  ratio: number;
};
export type PreShapeAttributeValue =
  | ExactValue
  | OriginalValue
  | TransitionValue;
export type FrameCollection = Record<
  string,
  { id: string; start: number; end: number }
>;
export type ActualFrame = {
  id: string;
  globalId: string;
  start: number;
  end: number;
  ratio: number;
};
export interface AnimationByStart {
  id: string;
  globalId?: string;
  start: number;
  end: number;
}
export interface AnimationItemByStart extends AnimationByStart {
  animation: AnimationItem;
}
export interface AnimationShapeAction {
  IRI: string;
  descriptor: {
    [attributeKey: string]: PreShapeAttributeValue;
  };
}

export interface AnimationConfigOpts {
  noDisappear?: boolean;
  text?: string;

  words?: TextWord[];
}

@Injectable()
export class AnimationService {
  originalDescriptors: Record<ShapeIRI, GeneralShapeDescriptor>;
  baseShapeTransforms: Record<ShapeIRI, ShapeTransform> = {};
  currentShapeTransforms: Record<ShapeIRI, ShapeTransform>;
  animationsByFrame: AnimationsByFrame = {};
  animationsByShape: AnimationsByShape = {};
  framesByImportedShapeIRI: Record<string, RootAnimationFrame> = {};
  functionFrames: Record<string, FunctionAnimationFrameObject[]> = {};
  selectedFunctionId: string;
  svgAttributes: Record<string, SVGAttributes> = {};
  colorPalette: Record<string, string>;
  openedFrames: FunctionAnimationFrameObject[] = [];
  get audioContext() {
    return this.audioService.audioContext;
  }
  get audioDestination() {
    return this.audioService.audioDestination;
  }
  get mainFrame() {
    return this.cs.previewShape?.animationFrame;
  }
  get rootShape() {
    return this.cs.previewShape;
  }
  get currentScene() {
    return this.cs.currentScene;
  }
  consequtiveActionsByShape: Record<
    string,
    Partial<Record<AnimationKeys, AnimationItemByStart[]>>
  > = {};
  consequtiveActionMap: Record<
    ShapeIRI,
    Partial<Record<AnimationKeys, Record<AnimationId, number>>>
  > = {};
  currentProject: Project;
  currentLanguage$: Observable<string>;
  currentLanguage: string;
  constructor(
    readonly store: Store,
    private readonly cs: CanvasService,
    public readonly audioService: AudioService,
    public readonly textAnimationService: TextAnimationService,
    public readonly backgroundService: BackgroundService,
  ) {
    gsap.registerPlugin(CustomEase);

    CustomEase.create(
      'custom',
      'M0,0 C0.256,-0.304 0.281,-0.068 0.5,0.5 0.768,1.198 0.819,1.25 1,1 ',
    );

    // -- // -- // -- // -- //
    this.store.select(originalDescriptors).subscribe(originalDescriptors => {
      this.originalDescriptors = originalDescriptors;
    });

    this.store.select(baseShapeTransforms).subscribe(shapeTransforms => {
      this.baseShapeTransforms = shapeTransforms;
    });

    this.currentLanguage$ = this.store.select(getCurrentLanguage);
    this.currentLanguage$.subscribe(lang => {
      this.currentLanguage = lang;
      this.mainFrame?.envChanged();
    });

    this.store
      .select(currentShapeTransforms)
      .subscribe(currentShapeTransforms => {
        this.currentShapeTransforms = currentShapeTransforms;
      });

    this.store.select(baseSvgAttributes).subscribe(svgAttributes => {
      this.svgAttributes = svgAttributes || {};
    });

    this.store.select(getCurrentColorPalette).subscribe(colorPalette => {
      this.colorPalette = colorPalette || {};
    });

    this.store.select(getCurrentProject).subscribe(currentProject => {
      this.currentProject = currentProject;
    });

    this.cs.keyEventSubscribe(
      'Space',
      async () => {
        switch (this.animationState) {
          case 'idle':
            await this.playAnimations();
            break;
          case 'paused':
            this.restartAnimation();
            break;
          case 'started':
            this.pauseAnimations();
            break;
        }
      },
      20,
    );

    this.cs.keyEventSubscribe('b+m', async () => {
      // -- // -- //
      this.mainFrame.frame.backgrounMusic = 'test';
      this.mainFrame.save();
      // -- //
    });

    this.cs.keyEventSubscribe('g+r', async () => {
      await this.cs.goToRecord();
    });

    // this.cs.generalEventSubscribe('play-animations', () =>
    //   this.playAnimations(),
    // );

    this.cs.keyEventSubscribe('s+f', () => {
      if (this.selectedFrame) {
        this.cs.consumeKeyEvent('f');
        this.selectedFrame.frame.soundAnimationFrame = {
          id: 'sound' + Math.random().toString(),
          duration: 1,
          paralell: {
            id: 'sound' + Math.random().toString(),
            duration: 2,
          },
        };
        this.selectedFrame.save();
      }
    });
  }
  animationFrameStore: Record<string, RootAnimationFrame> = {};
  frameByShape: Record<string, MainAnimationFrameObject>;
  get animationsByStart(): AnimationByStart[] {
    return this.mainFrame?.animationsByStart || [];
  }
  get animationTimeMap(): Record<
    AnimationId,
    {
      start: number;
      end: number;
    }
  > {
    return this.mainFrame?.animationTimeMap || {};
  }
  lastAnimationFrame: AnimationFrameObject;
  currentAnimationFrame: AnimationFrameObject;
  selectedFrame: AnimationFrameObject;
  ticker: Ticker;
  sum = 0;
  cnt = 0;
  delta = 0;
  frameCounter = 0;
  _currentTime = 0;
  set currentTime(time: number) {
    // -- // -- // -- // -- // -- //
    this.store.dispatch(setCurrentAnimationTime({ time }));
    this._currentTime = time;
  }
  get currentTime() {
    return this._currentTime;
  }
  animationFrames: Record<string, (increment: number) => void> = {};
  resolveColor(key: string) {
    if (key?.startsWith?.('<')) {
      const formula = key.slice(1);

      if (formula.startsWith('random')) {
        const [c1, c2, c3] = formula
          .slice(7)
          .split(',')
          .map(v => v.trim());
        // console.log('random-color', c1, c2, c3.slice(0, 7));
        // const color = ColorIncrementController.getColor(c1, c2.trim(), Math.random())
        // console.log('color', color);
        const num = Math.random();

        if (num < 0.333) {
          return c1;
        }
        if (num < 0.666) {
          return c2;
        }
        return c3.slice(0, 7);
      }

      return key;
    }

    if (!key?.startsWith?.('$')) {
      return key;
    }
    const colorPalette = this.currentProject.colorPalette;
    if (!colorPalette) {
      console.warn('color-palette-could not be found');
    }
    return colorPalette?.[key.slice(2)];
  }

  setScenes(rootShapes: FileRootShape[]) {
    // rootShapes.map(shape => {
    //   this.animationFrameStore[shape.IRI] = shape.animationFrame;
    // });
  }

  getAnimationsTillTime(
    time: number,
    animationsByStart: AnimationItemByStart[],
  ) {
    let ratio = 1;
    const animations = (animationsByStart || [])
      .filter(({ start, end }) => {
        if (end <= time) {
          return true;
        }
        if (time <= start) {
          return false;
        }

        ratio = (time - start) / (end - start);
        return true;
      })
      .map(({ animation }) => animation);

    return { animations, ratio };
  }

  getTransformTillTime(
    time: number,
    shapeIRI: string,
    originalValue: Translate | Rotation,
    key: keyof ShapeTransform = 'translate',
  ): Translate | Scale | Rotation {
    const { animations, ratio } = this.getAnimationsTillTime(
      time,
      this.consequtiveActionsByShape[shapeIRI]?.[key],
    );

    switch (key) {
      case 'translate':
      case 'scale':
        let { x: cx, y: cy } = originalValue as Translate;
        let currentRatio = 1;
        for (let i = 0; i < animations.length; i++) {
          const { value } = animations[i];
          const { x, y, relative } = this.resolveScale(value as Translate);
          if (ratio !== 1 && i == animations.length - 1) {
            currentRatio = ratio;
          }
          if (relative) {
            if (key == 'translate') {
              cx += currentRatio * x;
              cy += currentRatio * y;
            } else {
              cx *= 1 + currentRatio * (x - 1);
              cy *= 1 + currentRatio * (y - 1);
            }
          } else {
            if (currentRatio !== 1) {
              cx = cx + (x - cx) * ratio;
              cy = cy + (y - cy) * ratio;
            } else {
              cx = x;
              cy = y;
            }
          }
        }
        // -- // -- //
        return { x: cx, y: cy };
      case 'rotation':
        switch (animations.length) {
          case 0:
            return originalValue || { angle: 0 };
          default:
            return animations[animations.length - 1].value as Rotation;
        }
    }
  }

  resetBaseState() {
    if (this.animationState == 'started') {
      this.pauseAnimations();
    }

    this.animationState = 'idle';
    this.currentTime = 0;
    // console.log('reset-base-state > mainFrame', !!this.mainFrame); //
    this.mainFrame?.deselectAll();
    this.animationFrames = {};
    console.log('animation-service > resetBaseState');
  }

  getAbsolutePositionTillStartOfAnimation(
    animationId: string,
    shapeIRI: string,
    originalValue: Translate,
  ) {
    const { _start } = this.getFrameById(animationId);
    const { x, y } = this.getTransformTillTime(
      _start,
      shapeIRI,
      originalValue,
    ) as Translate;
    return { x, y };
  }

  getAbsolutePositionTillEndOfAnimation(
    animationId: string,
    shapeIRI: string,
    originalValue: Translate,
  ) {
    const { _end } = this.getFrameById(animationId);
    const { x, y } = this.getTransformTillTime(
      _end,
      shapeIRI,
      originalValue,
    ) as Translate;
    return { x, y };
  }
  getRelativeTranslateValue(
    animationId: string,
    shapeIRI: string,
    originalValue: Translate,
  ) {
    // -- // -- // -- //
    const { x: sx, y: sy } = this.getAbsolutePositionTillStartOfAnimation(
      animationId,
      shapeIRI,
      originalValue,
    );
    // -- // -- // -- //
    const { x: ex, y: ey } = this.getAbsolutePositionTillEndOfAnimation(
      animationId,
      shapeIRI,
      originalValue,
    );
    return { x: ex - sx, y: ey - sy };
  }
  animationState: 'idle' | 'started' | 'paused' = 'idle';
  get isAnimationStopped() {
    return this.animationState == 'paused';
  }

  timeLine: gsap.core.Timeline;

  sceneTimeOffset: number;

  get currentAnimationTime() {
    return this.sceneTimeOffset + (this.timeLine?.time() || 0);
  }

  resetAnimationTime() {
    this.sceneTimeOffset = 0;
    this.timeLine = null;
  }

  totalAnimationDuration: number;
  gsapAnimationIsPlaying: boolean;

  async initVideo(container: Container) {
    return new Promise<void>(resolve => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';

      video.src = 'https://private.cdn.inwedio.com/bg_video.mp4';
      // video.autoplay = true;
      // video.loop = true;
      video.muted = true; // usually required for autoplay
      video.playsInline = true;

      // video.loop = true;
      // video.autoplay = true;

      video.addEventListener('canplay', () => {
        // console.log('canplay', video.duration); //
        const texture = Texture.from(video, {
          resourceOptions: {
            crossorigin: 'anonymous', // important if not same-origin
            autoPlay: false, // don't start automatically
            preload: 'auto', // hint the browser
            updateFPS: 30, // how often to copy frames to the GPU
          },
        });

        // 3. Make a sprite //
        const sprite = new Sprite(texture);

        container.addChildAt(sprite, 0);
        video.play();
        resolve();
      });

      // -- //
    });
    // video.addEventListener('error', e => {
    //   console.error('Video error', video.error, e);
    // });
    // 2. Turn it into a PIXI texture
    // 4. Add the sprite to your container
  }

  isImageShape(shape: FileRootShape) {
    return (
      shape.IRI == 'http://nowords.com#5f7f8380-c452-11f0-961f-7114782dc989'
    );
  }

  gsapAnimateImage(shape: FileRootShape) {
    const scale = this.cs.fitScale;

    const targetScale = scale * 1.35;

    const dx =
      (this.cs._canvasWidth * scale - this.cs._canvasWidth * targetScale) / 2;
    const dy =
      (this.cs._canvasHeight * scale - this.cs._canvasHeight * targetScale) / 2;

    const timeLine = gsap.timeline();
    const zoomBack = gsap.timeline();

    timeLine.add(
      gsap.to(this.cs.canvasContainer.scale, {
        x: targetScale,
        y: targetScale,
        duration: 0.3,
        ease: 'none',
      }),
    );

    // console.log('dx, dy', dx, dy); // -- //

    timeLine.add(
      gsap.to(this.cs.canvasContainer.position, {
        x: dx,
        y: dy,
        duration: 0.3,
        ease: 'none',
      }),
      0,
    );

    zoomBack.add(
      gsap.to(this.cs.canvasContainer.scale, {
        x: scale,
        y: scale,
        duration: 0.3,
        ease: 'none',
      }),
    );

    zoomBack.add(
      gsap.to(this.cs.canvasContainer, {
        x: 0,
        y: 0,
        duration: 0.3,
        ease: 'none',
      }),
      0,
    );

    timeLine.add(zoomBack);

    return timeLine;
  }

  baseTimelines: gsap.core.Timeline[];
  sceneTransitionTimelines: gsap.core.Timeline[];

  async gsapAnimate(
    shape = this.cs.previewShape as FileRootShape,
    onlyScene = false,
  ) {
    this.sceneTimeOffset = shape.animationStartTime;

    if (this.animationState == 'paused') {
      this.animationState = 'started';
      this.timeLine?.play();
      return;
    }

    this.animationState = 'started';
    let current = shape;

    this.timeLine = gsap.timeline();

    this.baseTimelines = [];
    this.sceneTransitionTimelines = [];

    while (current) {
      current.gsapAnimate();
      this.sceneTransition(current);
      // const prev = current.prev as FileRootShape;
      // if (prev && prev.descriptor.textAnimationStep?.sceneTransition) {
      //   // the tricky part is that if the previous one has a scene transition
      //   // then the end of the timeline is (duration - (stDuration - stOverlap))
      //   const { overlap, duration } =
      //     prev.descriptor.textAnimationStep.sceneTransition;
      //   this.timeLine.add(timeline, `-=${duration}`);
      // } else {
      //   this.timeLine.add(timeline);
      // }

      if (onlyScene) {
        break;
      }

      // if (++i == 2) {
      //   break;
      // }
      // prev = current;
      current = current.next as FileRootShape;
    }

    this.timeLine.play();
  }

  makeOverlapTiming(n: number, total: number, overlap: number) {
    const d = total / (1 + (n - 1) * (1 - overlap));
    const gap = d * (1 - overlap);
    return { d, gap };
  }
  sceneTransition(current: FileRootShape) {
    const { sceneTransition } = current.descriptor.textAnimationStep || {};
    const {
      overlap,
      duration: baseDuration,
      from,
      delay,
      hideGloves,
      translate,
    } = sceneTransition || {};

    const duration = baseDuration || 0.5;

    const transitionTimeline = gsap.timeline();
    transitionTimeline.add(
      gsap.to(current.rootContainer, {
        duration,
        alpha: 0,
        ease: 'none',
      }),
    );

    if (translate) {
      console.log('sceneTransition > translates', translate);
      transitionTimeline.add(
        gsap.to(current.rootContainer.position, {
          duration,
          x: (i, target) => target.x + translate[0],
          y: (i, target) => target.y + translate[1],
          ease: 'power1.inOut',
        }),
        0,
      );
    }

    const next = current.next as FileRootShape;
    // const [dx, dy] = [0, 0];
    // if (from) {
    //   // -- // -- // -- // -- //
    //   // -- // -- // -- // -- // -- // -- //
    //   switch (from) {
    //     case 'left':
    //       dx = -_screenX;
    //       break;
    //     case 'right':
    //       dx = _screenX;
    //       break;
    //     case 'top':
    //       dy = -_screenY;
    //       break;
    //     case 'bottom':
    //       dy = _screenY;
    //       break;
    //   }

    //   transitionTimeline.add(
    //     gsap.to(current.rootContainer.position, {
    //       duration,
    //       x: (i, target) => target.x - dx,
    //       y: (i, target) => target.y - dy,
    //       ease: 'none',
    //     }),
    //   );
    // }
    // -- //
    if (next) {
      // -- //
      // if (dx || dy) {
      //   transitionTimeline.add(
      //     gsap.to(next.rootContainer.position, {
      //       duration,
      //       x: (i, target) => target.x - dx,
      //       y: (i, target) => target.y - dy,
      //       ease: 'none',
      //     }),
      //     0,
      //   );
      // }
      // -- //
      // transitionTimeline.eventCallback('onStart', () => {
      //   console.log('transitionTimeline > on-start');
      //   // next.rootContainer.position.x = dx;
      //   // next.rootContainer.position.y = dy;
      //   this.cs.canvasContainer.addChild(next.rootContainer);
      // });
    }

    transitionTimeline.eventCallback('onComplete', () => {
      this.cs.canvasContainer.removeChild(current.rootContainer);
    });

    if (hideGloves) {
      transitionTimeline.add(this.backgroundService.hideGloves(duration));
    }

    this.timeLine.add(
      transitionTimeline,
      overlap ? `-=${overlap}` : delay ? `+=${delay}` : undefined,
    );
    // -- //
  }

  // }
  async pauseGsapAnimate() {
    if (this.animationState == 'paused') {
      return;
    }
    this.timeLine?.pause();
    this.animationState = 'paused';
  }
  regenerateAnimation(current: NewTextAnimation, opts?: AnimationConfigOpts) {
    // this.cs.currentScene
    // this.store.dispatch(
    //   setDescriptorValue({
    //     IRI: this.cs.currentRootShape.IRI,
    //     key: 'newAnimation',
    //     value: null,
    //     direct: true,
    //   }),
    // );

    let newAnimation: NewTextAnimation;

    // return this.getNewTextAnimationById('section-zoom-in-out-then-extend');
    // console.log('regenerateAnimation', current);

    // if (current.id == 'section-zoom-in-out-then-extend') {
    //   return this.getNewTextAnimationById('fade-in-zoom-in');
    // } else {
    //   return this.getNewTextAnimationById('section-zoom-in-out-then-extend');
    // }

    switch (current.id) {
      case 'left':
        newAnimation = this.getNewTextAnimationById(
          'section-zoom-in-out-then-extend',
          opts,
        );
        break;
      case 'section-zoom-in-out-then-extend':
        newAnimation = this.getNewTextAnimationById('up-with-word', opts);
        break;
      case 'up-with-word':
        newAnimation = this.getNewTextAnimationById('fade-in', opts);
        break;
      case 'fade-in':
        newAnimation = this.getNewTextAnimationById('fade-in-zoom-in', opts);
        break;
      case 'fade-in-zoom-in':
      default:
        newAnimation = this.getNewTextAnimationById('left', opts);
        break;
    }

    return newAnimation;
  }

  getNewTextAnimation(text?: string) {
    // -- //

    const baseTransform: 'translate' | 'zoom' = 'translate' as any;

    const segment = Math.random() < 0.4;

    // -- //

    switch (baseTransform) {
      case 'translate':
        // -- //
        const from: 'left' | 'right' | 'top' | 'bottom' = 'left' as any;
        // -- //
        return {
          baseType: 'appear',
          duration: 1.2,
          line: {
            translate: {
              from,
              distance: 1.5,
            },
          },
        };

      case 'zoom':
        return {
          baseType: 'appear',
          duration: 1.2,
          line: {
            zoom: {
              from: 2,
            },
          },
        };
        break;
    }
  }

  getNewImageAnimationById(id: ImageAnimationId): ImageAnimationDescriptor {
    switch (id) {
      case 'float-left':
        return {
          id,
          baseType: 'appear',
          duration: 1.2,
          float: 'left',
        };
      case 'float-right':
        return {
          id,
          baseType: 'appear',
          duration: 1.2,
          float: 'right',
        };
      case 'float-top':
        return {
          id,
          baseType: 'appear',
          duration: 1.2,
          float: 'top',
        };
      case 'float-bottom':
        return {
          id,
          baseType: 'appear',
          duration: 1.2,
          float: 'bottom',
        };
      case 'zoom-in':
        return {
          id,
          baseType: 'appear',
          duration: 1.2,
          scale: {
            from: 0.5,
            to: 1,
          },
        };
    }
  }

  getNewTextAnimationById(
    id: NewAnimationId,
    opts: AnimationConfigOpts,
  ): NewTextAnimation {
    // console.log('getNewTextAnimationById', id); // -- //
    const { text, words } = opts || {};
    // -- // -- //
    switch (id) {
      case 'word-one-by-one':
        return {
          id,
          baseType: 'appear',
          duration: 5,
          word: {
            oneByOne: true,
          },
        };
      case 'by-word':
        return {
          id,
          baseType: 'appear',
          duration: 3,
          // line: {
          //   type: 'fade',
          //   zoom: {
          //     to: 1.2,
          //   },
          // line: {},
          // },
          // zoom: {
          //   duration: 1.2,
          //   from: 0.5,
          //   to: 1.2,
          //   noFade: true,
          // },
          translate: {
            dx: 0,
            dy: 220,
            duration: 3,
            fadeDuration: 1,
          },
          word: {
            overlap: 0.4,
            // delay: 0.5,
          },
          // next: {
          //   baseType: 'disappear',
          //   duration: 0.5,
          // },
        };
        break;
      case 'fade-in':
        return {
          id,
          baseType: 'appear',
          duration: 1.6,
          line: {
            type: 'fade',
            zoom: {
              to: 1.2,
            },
          },
          next: {
            baseType: 'disappear',
            duration: 1,
            line: {
              translate: {
                from: 'bottom',
                distance: 2,
              },
            },
            // line: {
            //   zoom: {
            //     to: 0.8,
            //   },
            // },
          },
        };
      case 'zoom-out-in':
        return {
          id,
          baseType: 'appear',
          duration: 0.6,
          zoom: {
            from: 0.5,
            to: 1,
          },
          // line: {
          //   zoom: {
          //     to: 1.6,
          //   },
          // },
          next: {
            // here comes the action in the middle //
            baseType: 'disappear',
            duration: 0.6,
            zoom: {
              to: 0.5,
            },
            // next: {
            //   baseType: 'disappear',
            //   duration: 0.6,
            // },
            // baseType: 'disappear',
            // duration: 0.6,
          },
        };
      case 'fade-in-zoom-in':
        // console.log('case-fade-in-zoom-in');
        return {
          id,
          baseType: 'appear',
          duration: 1,
          line: {
            // type: 'fade',
            segment: [0, 3],
            zoom: {
              to: 2.5,
            },
          },
          next: {
            duration: 2,
            gradientMask: {
              colors: ['#e12d6cff', '#2858dbff'],
              targetWord: 4,
              delay: 1,
              duration: 2,
            },
            // delay: 1.2,
            // overlap: 0.5,
            line: {
              duration: 4,
              action: {
                type: 'show-segments',
                segment: [3, words.length],
                // overlap: 0.3,
                targetTranslate: -1000,
                // translateDuration: 0.7,
              },
            },
            next: {
              baseType: 'disappear',
              duration: 1,
              line: {},
            },
            // next: {
            //   baseType: 'disappear',
            //   duration: 1,
            //   line: {
            //     duration: 2,
            //     translate: {
            //       from: 'right',
            //       distance: 2,
            //     },
            //   },
            // },
          },
        };
      case 'up-with-word':
        return {
          id: 'up-with-word',
          baseType: 'appear',
          duration: 1.2,
          line: {
            // type: 'fade',
            translate: {
              from: 'top',
              distance: 1,
            },
          },
          word: {
            // delay: 0.5,
            duration: 1.2,
            translate: {
              from: 'top',
              distance: 0.4,
            },
          },
          next: !opts?.noDisappear && {
            baseType: 'disappear',
            duration: 0.5,
            line: {
              // duration: 2,
              // translate: {
              //   from: 'top',
              //   distance: 1,
              // },
            },
            // word: {
            //   translate: {
            //     from: 'top',
            //     distance: 0.4,
            //   },
            // },
          },
        };
      case 'section-zoom-in-out-then-extend':
        return {
          id: 'section-zoom-in-out-then-extend',
          baseType: 'appear',
          duration: 0.5,
          line: {
            segment: [0, 1],
            zoom: {
              to: 2,
            },
          },
          next: {
            duration: 0.7,
            baseType: 'action',
            line: {
              action: {
                type: 'zoom',
                magnitude: 1,
              },
            },
            next: {
              baseType: 'action',
              overlap: 0.2,
              ease: 'none',
              duration: 0.6,
              line: {
                action: {
                  type: 'show-segments',
                  segment: [1, words.length],
                  targetScale: 1,
                  overlap: 0.3,
                  // translateDuration: 0.7,
                },
              },
              next: {
                baseType: 'disappear',
                duration: 10,
                line: {},
              },
            },
          },
        };
      case 'left':
      default:
        return {
          id: 'left',
          baseType: 'appear',
          duration: 1.5,
          // ease: 'none',
          line: {
            // ease: 'none',
            // type: 'fade',
            translate: {
              from: 'right',
              distance: 2,
            },
          },
          word: {
            // delay: 0.5,
            // overlap: 0.1,
            // duration: 1.2,
            translate: {
              from: 'right',
              distance: 1.5,
            },
          },
          next: !opts?.noDisappear && {
            baseType: 'disappear',
            duration: 0.5,
            line: {
              duration: 2,
              translate: {
                from: 'right',
                distance: 1,
              },
            },
            word: {
              translate: {
                from: 'right',
                distance: 0.4,
              },
            },
          },
        };
    }
  }

  /*

    zoom-in 
      fade-in
      show-word-by-word
  */

  start: number;
  startTicker() {
    if (!this.cs.app) {
      return;
    }
    this.start = performance.now();
    this.frameCounter = 0;
    this.ticker = this.cs.app.ticker.add(this.tickerHandler, this);
  }

  get currentTimeInSec() {
    return this.currentTime / 60;
  }

  get currentTimeTexts() {
    const sec = Math.floor(this.currentTimeInSec);
    const min = Math.floor(sec / 60);
    const convert = (num: number) =>
      num < 10 ? `0${Math.floor(num)}` : Math.floor(num);
    const convert100 = (num: number) => {
      if (num > 999) {
        return num.toString().slice(0, 3);
      }
      if (num > 99) {
        return num;
      }
      if (num > 10) {
        return `0${num}`;
      }
      return `00${num}`;
    };
    const milisec = Math.floor((this.currentTime - 60 * sec) * 16.666667);
    return {
      min: `${convert(min)}`,
      sec: `${convert(sec)}`,
      milisec: `${convert100(milisec)}`,
    };
  }

  tickerHandler(delta: number) {
    this.frameCounter += 1;
    this.currentTime += delta;
    this.delta += delta;

    // const timeCount = Math.floor(this.timeCnt);
    // if (timeCount % 60 == 0 || timeCount % 60 == 1) {
    //   console.log(
    //     'current-time',
    //     timeCount,
    //     (performance.now() - this.start) / 1_000,
    //   );
    // }

    const _delta = Math.round(this.delta);

    if (this.frameCounter == 2) {
      Object.values(this.animationFrames).map(fcn => fcn(_delta));
      this.delta = 0;
      this.frameCounter = 0;
    }
  }
  pauseAnimations() {
    this.animationState = 'paused';
    this.audioService.pauseBackgroundMusic();
    this.cs.generalEventEmit('animation-paused');
    this.removeTicker();
  }
  removeTicker() {
    this.ticker.remove(this.tickerHandler, this);
  }
  restartAnimation() {
    console.log('restart-animation');
    this.cs.generalEventEmit('animation-restarted');
    this.animationState = 'started';
    this.audioService.restartBackgroundMusic();
    this.addTicker();
  }

  addTicker() {
    // -- //
    this.ticker = this.cs.app.ticker.add(this.tickerHandler, this);
  }

  async playAnimations() {
    return;
    this.animationState = 'started';
    await this.audioService.waitForAudioFiles();
    this.startTicker();
    this.audioService.startBackgroundMusic();
    // console.log('playAnimations', this.selectedFrame);
    // -- // -- //

    this.cs.generalEventEmit('animation-started');

    if (this.selectedFrame) {
      if (this.selectedFrame.next) {
        // const start = this.selectedFrame._start;
        // this.cs.previewShape.applyAnimationByTime(start);
        await this.selectedFrame.next.animate();
      } else {
        console.warn('There is nothing to animate!');
      }
    } else {
      console.log('main-frame > animate', this.mainFrame);
      this.cs.previewShape.__setPreAnimationState();
      await this.mainFrame?.animate();
    }

    this.removeTicker();
    this.animationState = 'idle';

    this.lastAnimationFrame?.select();
    this.cs.generalEventEmit('animation-finished', Math.random());
    this.cs.previewShape.shapes.map(shape => shape.updateRectangleController());
    console.log('playAnimations > finished');
  }

  async playSlide() {
    // -- //

    if (this.animationState == 'started') {
      return;
    }
    this.animationState = 'started';

    this.startTicker();
    this.cs.generalEventEmit('animation-started');

    const frame = this.getFrameToPlay({ slideMode: true });
    await frame.animate();
    this.removeTicker();
    this.animationState = 'idle';

    this.rootShape.resetBaseState();

    this.cs.generalEventEmit('animation-finished', Math.random());
    this.cs.previewShape.shapes.map(shape => shape.updateRectangleController());
    console.log('playAnimations > finished');
  }

  getFrameToPlay({ slideMode }: { slideMode?: boolean } = {}) {
    if (slideMode) {
      const frame = this.rootShape.getContainerAnimatioFrame();
      const animationFrame = new RootAnimationFrame(this.rootShape, frame);
      this.rootShape.__setPreAnimationState();
      return animationFrame;
    }

    if (this.selectedFrame) {
      return this.selectedFrame;
    }

    return this.mainFrame;
  }

  animate(cb: (increment: number) => boolean) {
    return new Promise<void>(resolve => {
      const id = Math.random().toString();
      this.animationFrames[id] = (increment: number) => {
        const finished = cb(increment);
        if (finished) {
          delete this.animationFrames[id];
          resolve();
        }
      };
    });
  }

  stopAnimation(id: string) {
    delete this.animationFrames[id];
  }

  getRootAnimationId(id: string) {
    if (!id) {
      return '';
    }
    const ids = id.split('_');
    return ids[ids.length - 1];
  }
  setAnimationItem(
    shapeIRIs: string[],
    item: AnimationItem,
    animationId: string,
  ) {
    const currentFrame = this.getFrameById(animationId);
    if (!currentFrame) {
      return;
    }
    const { _end } = currentFrame;

    shapeIRIs.map(IRI => {
      _set(this.animationsByShape, [IRI, animationId, item.key], item);
      _set(this.animationsByFrame, [animationId, IRI, item.key], item);
      this.setConsequtiveAction(IRI, item.key, animationId, item);
    });
    const updates = {};
    shapeIRIs.map(IRI => {
      updates[IRI] = {
        [item.key]: this.getBulkUpdateValue(IRI, item.key, _end) || item.value,
        //   [item.key]: {
        //     value: this.getBulkUpdateValue(IRI, item.key, _end) || item.value,
        //   },
      };
    });

    return updates;
  }

  getTextAnimation() {
    // -- //
    // Case 1.
    // Appear
    // From the top the lines floats down and the words gradual appearance -> from to is the valid one
    // Disappear
    // The lines floats down and the words gradual disappearance -> same way float down

    let type: any;

    switch (type) {
      case 'line-floats-in-from-the-top-bottom':
        // -- //
        // solve this two top/bottom
        const lineFloatsInFromTheTop_v1: NewTextAnimation = {
          baseType: 'appear',
          duration: 1,
          line: {
            translate: {
              from: 'top',
              distance: 3,
            },
          },
          word: {
            translate: {
              from: 'top',
              distance: 1,
            },
          },
          next: {
            baseType: 'disappear',
            duration: 1,
            word: {
              translate: {
                from: 'top',
                distance: 1,
              },
            },
          },
        };

        // Disappear

        const disappears = {
          'line-disappears': {
            baseType: 'disappear',
          },
          'line-disappear-float-down': {
            baseType: 'disappear',
            line: {
              translate: {
                from: 'top',
              },
            },
          },
          'line-disappear-by-word': {
            baseType: 'disappear',
            word: {},
          },
          'line-disppeaar-by-word-floats-down': {
            baseType: 'disappear',
            word: {
              translate: {
                from: 'bottom',
                distance: 1,
              },
            },
          },
        };

        const disappear = {
          baseType: 'disappear',
        };

        // -- // -- //

        break;

      case 'line-fades-in-fades-out':
        const lineFadesIn_v1 = {
          baseType: 'appear',
          duration: 1,
          line: {
            type: 'fade',
            zoom: {
              from: 2,
            },
          },
          next: {
            baseType: 'disappear',
            duration: 1,
            line: {
              type: 'fade',
            },
          },
        };

        // The zoom should not change the fade mechanism
        break;

      case 'line-floats-in-from-the-right-to-the-left':
        // -- //
        const lineFloatsInFromTheLeft_v1 = {
          baseType: 'appear',
          duration: 1,
          line: {
            translate: {
              from: 'left',
              distance: 1,
            },
          },
          word: {
            translate: {
              from: 'left',
              distance: 1,
            },
            ease: 'power1.inOut',
          },
          next: {
            baseType: 'disappear',
            duration: 1,
          },
        };

        // We can realize that the same time words

        let wordType: any;
        switch (wordType) {
          case 'translate':
            // base
            break;
          case 'fade':
            // This works only from the right to the left
            // But it can work
            break;
          case 'zoom':
            // zoom-ca work
            break;
        }

        break;

      case 'line-segment-zooms-in-fast':
        // -- //
        // ---------------------------------------------------------------------------------------- //
        // https://hu.pinterest.com/pin/473652085833235258/
        // zooms in and really quick appear/disappear pairs to change words and the action is zoom-in
        // we might need define background switch type - inverse
        // (each animation should have its light and dark colors for the inverse action)

        // Solution
        // It can be a function.
        // Input sentenct -> wordBased zoom-in

        // here we have segments to show [0, 2] -> [2, 3] -> [3, 5] etc.
        // each has a type of appearance.
        // and each has a type of action (mostly slow zoom, or some color magic)
        const lineSegment = {
          baseType: 'appear',
        };

        // Task -> go for the zoom based one
        // But this can be anything

        let mode: any;
        switch (mode) {
          case 'zoom':
            break;
          case 'translate':
            break;
        }

        const lineSegment_v1 = {
          baseType: 'appear',
          line: {
            zoom: {
              from: 0.5,
            },
            segment: [0, 2],
          },
          next: {
            // This leads to the quick disappear of the previous segment
            baseType: 'appear',
            line: {
              segment: [2, 3],
              zoom: {
                from: 0.5,
              },
            },
          },
        };

        break;

      case 'zoom-in-segment-than-shift':
        // -- // -- //
        const zoomInSegmentThanShift = {
          baseType: 'appear',
          line: {
            zoom: {
              from: 1.5,
            },
            segment: [0, 2],
          },
          next: {
            baseType: 'appear',
            line: {
              segment: [2, 5],
              type: 'fade',
            },
          },
        };

        // https://hu.pinterest.com/pin/117164027801362494/
        // One word zooms-out and in and after the rest of the text appears

        const oneWordZoomsOutAndIn = {
          baseType: 'appear',
          line: {
            zoom: {
              to: 1.5,
            },
            segment: [0, 1],
          },
          next: {
            baseType: 'action',
            line: {
              zoom: {
                to: 1.1,
              },
            },
            next: {
              baseType: 'appear',
              line: {
                segment: [1, 5],
                type: 'fade',
              },
            },
          },
        };
        break;

      case 'shift':
        // Lets have a simpler descriptor

        // https://hu.pinterest.com/pin/67694800643742647/

        // Intense word/section base. Some cases zoom-in based, sometimes just appears
        //hu.pinterest.com/pin/39617671718552915/

        const desc = {
          type: 'line-segmentation',
          actions: [
            {
              segment: [0, 3],
              zoom: {
                from: 0.8,
              },
            },
            {
              shift: true,
              zoomToFit: true,

              till: -1, // all
              zoom: {
                from: 0.8,
              },
            },
          ],
        };

        break;
    }

    // ZOOM IN

    // https://hu.pinterest.com/pin/4855512094750918/
    const zoomIn_v1 = {
      baseType: 'appear',
      line: {
        zoom: {
          from: 2,
          // or possibly from: 0.5
          // to: 2 (maybe both is defined)
        },
        segment: {
          type: 'word',
          count: 3,
        },
      },
      next: {
        baseType: 'appear',
        line: {
          shift: {
            target: [0, 4], // can be [3, 3]
          },
          word: {
            type: 'fade', // or the default is one by one
          },
          // !!! The tricky part, if the zoom.to is i.e 2 then we shift the word so
          // not the whole text is seen
        },
      },
    };

    // MULTI LINE
    // https://hu.pinterest.com/pin/324259241944063095/

    const multiLine_v1 = {
      // -- // this is the matter of initial line break
      word: {
        type: 'fade',
        translate: {
          from: 'left',
          distance: 1,
        },
      },
    };

    // That way I can split up a line to show by certain word sections

    //--------------- not supported yet ---------------------------- //
    // https://hu.pinterest.com/pin/50806302042039100/
    // Letter drop shadow -> can be a good one, but current

    //https://hu.pinterest.com/pin/139682025938480872/
    // cool gradient background
  }

  setAnimationItemSubvalue(
    shapeIRIs: string[],
    key: AnimationKeys,
    innerKey: AnimationInnerKey,
    value: PrimitiveValue,
    animationId: string,
  ): { animations: AnimationBatch; bulkUpdates: any } {
    // -- // -- // -- //
    let bulkUpdates = {};
    const animations: AnimationBatch = {};
    shapeIRIs.map(IRI => {
      const currentAnimationItem =
        this.animationsByShape[IRI]?.[animationId]?.[key] ||
        ({
          key,
          value: {
            [innerKey]: value,
          },
        } as AnimationItem);

      /* console.log(
        'setAnimationItemSubvalue > currentAnimationItem',
        currentAnimationItem,
      ); */
      let newItem;

      if (key == 'translate' && innerKey == 'relative') {
        if (value == true) {
          // -- // -- // -- //
          const { x, y } = this.getRelativeTranslateValue(
            animationId,
            IRI,
            this.baseShapeTransforms[IRI].translate,
          );

          const item = {
            key,
            value: {
              x,
              y,
              relative: true,
            },
          };
          _set(animations, [IRI, animationId, item.key], item);

          this.setAnimationItem(
            [IRI],
            {
              ...currentAnimationItem,
              value: {
                x,
                y,
                relative: true,
              },
            },
            animationId,
          );
        } else {
          const item = {
            ...currentAnimationItem,
            value: {
              ...this.currentShapeTransforms[IRI].translate,
              relative: false,
            },
          };

          this.setAnimationItem([IRI], item, animationId);

          _set(animations, [IRI, animationId, item.key], item);
        }
      } else {
        // console.log('1', currentAnimationItem);
        const item = {
          ...currentAnimationItem,
          value: {
            ...(currentAnimationItem.value as Record<string, PrimitiveValue>),
            [innerKey]: value,
          },
        };

        newItem = this.setAnimationItem([IRI], item, animationId);

        _set(animations, [IRI, animationId, item.key], item);

        // animations.push({
        //   IRI,
        //   animationsById: {
        //     [animationId]: [item],
        //   },
        // });
      }

      bulkUpdates = {
        ...bulkUpdates,
        ...(newItem || {}),
      };
    });

    this.setAnimationStore(animations);

    return { animations, bulkUpdates };
  }
  setAnimationStore(animations: AnimationBatch) {
    Object.entries(animations).map(([IRI, animationsById]) => {
      Object.entries(animationsById || {}).map(
        ([animationId, animationArray]) => {
          Object.values(animationArray).map((item: AnimationItem) => {
            _set(this.animationsByFrame, [animationId, IRI, item.key], item);
            _set(this.animationsByShape, [IRI, animationId, item.key], item);
          });
        },
      );
    });
  }
  removeAnimationItem(key: AnimationKeys, IRIs: string[], animationId: string) {
    IRIs.map(IRI => {
      this.deleteFromConsequtiveActions(IRI, key, animationId);
    });

    const updates = {};
    IRIs.map(IRI => {
      // this.deleteFromStore(IRI, animationId, key);
      switch (key) {
        case 'translate':
          updates[IRI] = {
            translate: this.getAbsolutePositionTillEndOfAnimation(
              animationId,
              IRI,
              this.baseShapeTransforms[IRI].translate,
            ),
          };
          break;
      }
    });

    return updates;
  }
  getBulkUpdateValue(shapeIRI: string, key: AnimationKeys, time: number) {
    // -- // -- // -- //
    switch (key) {
      case 'translate':
      case 'scale':
        return this.getTransformTillTime(
          time,
          shapeIRI,
          this.resolveScale(this.baseShapeTransforms[shapeIRI]?.[key]),
          key,
        );
    }
  }

  setConsequtiveActionsByShape(
    shapeIRI: string,
    consequtiveActions: ConsequtiveActionByKey,
  ) {
    this.consequtiveActionsByShape[shapeIRI] = consequtiveActions;
    this.consequtiveActionMap[shapeIRI] = {};
    Object.keys(consequtiveActions).map(animationKey =>
      this.updateConsequtiveActionMap(shapeIRI, animationKey as AnimationKeys),
    );
  }
  setConsequtiveAction(
    shapeIRI: string,
    animationKey: AnimationKeys,
    animationId: string,
    animation: AnimationItem,
    globalId?: string,
  ) {
    const currentFrame = this.getFrameById(animationId);

    if (!currentFrame) {
      throw new Error(
        `AnimationService.addAdnimation - no frame was found with id: ${animationId}`,
      );
    }

    if (animationId.includes('_')) {
      globalId = animationId;
      animationId = this.getRootAnimationId(animationId);
    }

    const { _start, _end } = currentFrame;
    this.consequtiveActionsByShape[shapeIRI] ||= {};
    this.consequtiveActionMap[shapeIRI] ||= {};
    this.consequtiveActionsByShape[shapeIRI][animationKey] ||= [];

    const index =
      this.consequtiveActionMap[shapeIRI][animationKey]?.[animationId];
    if (index !== undefined) {
      // This is the update case
      this.consequtiveActionsByShape[shapeIRI][animationKey][index].animation =
        animation;
      return;
    }

    const prevs = this.consequtiveActionsByShape[shapeIRI][animationKey].filter(
      ({ end }) => end <= _start,
    );
    const posts = this.consequtiveActionsByShape[shapeIRI][animationKey].filter(
      ({ start }) => _end <= start,
    );

    this.consequtiveActionsByShape[shapeIRI][animationKey] = [
      ...prevs,
      {
        id: animationId,
        globalId,
        start: _start,
        end: _end,
        animation,
      },
      ...posts,
    ];

    this.updateConsequtiveActionMap(shapeIRI, animationKey);
  }

  getFrameById(animationId: string) {
    // return this.mainFrame.getFrameById(animationId);
    return this.mainFrame?.store[animationId];
  }

  deleteFromConsequtiveActions(
    shapeIRI: string,
    animationKey: AnimationKeys,
    animationId: string,
  ) {
    const index =
      this.consequtiveActionMap[shapeIRI][animationKey][animationId];
    if (index == undefined) {
      // throw new Error(`Consequtive animation map has not been found!`);
    } else {
      this.consequtiveActionsByShape[shapeIRI][animationKey].splice(index, 1);
    }

    this.updateConsequtiveActionMap(shapeIRI, animationKey);
  }

  updateConsequtiveActionMap(shapeIRI: string, animationKey: AnimationKeys) {
    /*
    this.consequtiveActionMap[shapeIRI][animationKey] = {};
    this.consequtiveActionsByShape[shapeIRI][animationKey].map(
      ({ id, globalId }, index) => {
        this.consequtiveActionMap[shapeIRI][animationKey][globalId || id] =
          index;
      },
    ); */
  }

  incrementAnimationState(shapeIRI: string, animationKey: AnimationKeys) {
    // -- // -- //
  }

  getWaterfallByDistance(length: number, n: number, d: number) {
    const overlap = (length - n * d) / (1 - n);
    const result = [];
    for (let i = 0; i < n; i++) {
      const start = i * (d - overlap);
      result.push({
        start,
        end: start + d,
      });
    }
    return result;
  }

  getWaterfallByOverlap(
    length: number,
    n: number,
    overlap: number,
  ): Array<{ start: number; end: number }> {
    const d = (length - (1 - n) * overlap) / n;
    // -- // -- // -- // -- //
    console.log({ length, n, overlap, d });
    const result = [];
    for (let i = 0; i < n; i++) {
      const start = i * (d - overlap);
      result.push({
        start,
        end: start + d,
      });
    }
    return result;
  }

  resetFrame(
    animationFrame: AnimationFrame,
    shape?: GeneralShape,
    main = false,
  ) {}

  getShapeAttributeValue(
    key: string,
    actions: AnimationAction[],
    originalValue?: any,
  ) {
    if (!actions.length) {
      console.log('original-value', originalValue);
      return originalValue;
    }
    // console.log({ key, actions: JSON.stringify(actions, null, 2), originalValue }); //
    if (!originalValue) {
      return {};
    }

    let { x, y, relative: rel } = originalValue as Translate;

    // if I get the initial value in relative -> I shall return in relative
    let [prevX, prevY] = [x, y];
    switch (key) {
      case 'rotation':
        return actions[actions.length - 1].value as Rotation;
      case 'scale':
        let [ox, oy] = [0, 0];
        for (const action of actions) {
          const { value, ratio } = action;
          const { x: _x, y: _y, relative, center } = value as _Scale;
          if (relative) {
            x *= _x * ratio;
            y *= _y * ratio;
          } else {
            if (ratio !== 1) {
              // it would work for the other branch as well
              x = x + (_x - x) * ratio;
              y = y + (_y - y) * ratio;
            } else {
              x = _x;
              y = _y;
            }
          }

          if (center) {
            ox = (x - prevX) / 2;
            oy = (y - prevY) / 2;
          }

          prevX = x;
          prevY = x;
        }
        // console.log('get-shape-attr-value > scale', { x, y });
        return {
          x,
          y,
          offset: ox || oy ? [ox, oy] : undefined,
          relative: rel,
        };

      case 'translate':
        for (const action of actions) {
          const { value, ratio } = action;
          const {
            x: _x,
            y: _y,
            relative,
          } = value as Translate & { relative: boolean };
          if (relative) {
            x += _x * ratio;
            y += _y * ratio;
          } else {
            if (ratio !== 1) {
              // it would work for the other branch as well
              x = x + (_x - x) * ratio;
              y = y + (_y - y) * ratio;
            } else {
              x = _x;
              y = _y;
            }
          }
        }
        return {
          x,
          y,
        };

      case 'show-hide':
        const { value: show } = actions[actions.length - 1]
          .value as ShowHideAnimation;
        return show;

      case 'fill':
        const fill = actions[actions.length - 1].value as string;
        return fill.startsWith('$')
          ? this.colorPalette[fill.substring(2)]
          : fill;
      case 'stroke':
        const stroke = actions[actions.length - 1].value as StrokeConfig;
        return {
          color: this.colorPalette[stroke.color.substring(2)] || stroke.color,
          width: stroke.width,
        };
      // case 'path':
      default:
        const pathAnimation = actions[actions.length - 1].value;
        return pathAnimation;
    }

    return;
    const last = actions.pop();
    if (!last) {
      return;
    }
    let { ratio, value } = last;
    const relative = true;
    if (key == 'position') {
      // -- //
      let dx = 0;
      let dy = 0;
      let X: number, Y: number;
      let RATIO = 0;
      let { x, y } = originalValue as ShapePosition;

      while (1) {
        if (relative) {
          dx += (value as ShapePosition).x * ratio;
          dy += (value as ShapePosition).y * ratio;
        } else {
          const position = value as ShapePosition;

          if (ratio == 1) {
            x = position.x;
            y = position.y;
            break;
          }

          X = position.x;
          Y = position.y;
          RATIO = ratio;
        }
        const next = actions.pop();
        if (!next) {
          break;
        }
        ratio = next.ratio;
        // relative = next.relative;
        value = next.value;
      }

      if (X !== undefined) {
        const [lastAbsX, lastAbsY] = [x + dx, y + dy];
        return {
          x: lastAbsX + (X - lastAbsX) * RATIO,
          y: lastAbsY + (Y - lastAbsY) * RATIO,
        };
      } else {
        return {
          x: x + dx,
          y: y + dy,
        };
      }
    } else {
      if (last.ratio == 1) {
        return value;
      } else {
        const numberKeys = {
          opacity: true,
          'fill-opacity': true,
          'stroke-opacity': true,
        };

        const lastButOne = actions.pop();

        if (lastButOne) {
        } else {
        }

        return {
          type: 'transition',
          ratio,
          from: lastButOne
            ? {
                type: 'exact',
                value: lastButOne.value,
              }
            : {
                type: 'original',
              },
          to: {
            type: 'exact',
            value,
          },
        };
      }
    }
  }

  newFrameObject<T = AnimationFrameObject>(
    frame: AnimationFrame,
    relationships: AnimationFrameRelationships,
  ): T {
    frame ||= {};
    frame.id ||= Math.random().toString();
    frame.duration = isNaN(frame.duration) ? 1 : frame.duration;

    if (this.cs.isSpacePressed && relationships.prev) {
      frame.duration = relationships.prev.duration;
    }
    // -- //
    const newFrame = this.initFrameObject(frame, relationships);
    return newFrame as T;
  }

  initFrameObject(
    frame: AnimationFrame,
    relationships: AnimationFrameRelationships = {},
  ) {
    let frameObject: AnimationFrameObject;
    switch (frame.type) {
      // case 'appear':
      // case 'disappear':
      //   frameObject = new BaseAnimationFrameObject(
      //     frame as TextAnimationFrame,
      //     relationships,
      //   );

      // break;
      case 'text':
        frameObject = new TextAnimationFrameObject(
          frame as TextAnimationFrame,
          relationships,
        );
        break;
      case 'auto-animation':
        // -- // -- //
        frameObject = new AutoAnimationFrameObject(
          frame as AutoAnimationFrame,
          relationships,
        );
        break;
      case 'local-sound':
        // console.log('init-local-sound');
        frameObject = new LocalSoundAnimationFrameObject(
          frame as TextAnimationFrame,
          relationships,
          this.audioContext,
          this.audioDestination,
        );
        break;
      case 'canvas-transform':
        frameObject = new CanvasTransformFrameObject(
          frame as CanvasTransformFrame,
          relationships,
        );
        break;
      case 'scene-transition':
        frameObject = new SceneTransitionFrameObject(
          frame as CanvasTransformFrame,
          relationships,
        );
        // -- // -- // -- // -- //
        break;
      case 'base':
        frameObject = new BaseAnimationFrameObject(
          frame as BaseAnimationFrame,
          relationships,
        );
        break;
      default:
        if ((frame as SoundAnimationFrame).soundFileUrl) {
          frameObject = new SoundAnimationFrameObject(frame, relationships);
        } else if (frame.function) {
          frameObject = new FunctionAnimationFrameObject(frame, relationships);
        } else if ((frame as SubSceneTransitionFrame).targetSubScene) {
          console.log('---- subscene-transition -----', frame);
          frameObject = new SubSceneTransitionFrameObject(
            frame as SubSceneTransitionFrame,
            relationships,
          );
        } else if (frame.inverse) {
          frameObject = new InverseAnimationFrameObject(frame, relationships);
          // -- // -- //
        } else if (frame.stateTransition) {
          frameObject = new SceneTransitionFrameObject(frame, relationships);
        } else {
          frameObject = new AnimationFrameObject(frame, relationships);
        }
    }

    // if (parent) {
    //   parent.child = frameObject;
    // }

    // if (prev) {
    //   prev.next = frameObject;
    // }

    frameObject.init();
    return frameObject;
  }

  currentFrame: MainAnimationFrameObject;

  addFirstFrame() {
    // -- // -- // -- // -- //
    this.currentFrame = new RootAnimationFrame(this.rootShape, {
      id: Math.random().toString(),
      duration: 1,
    });

    this.rootShape.animationFrame = this.currentFrame as RootAnimationFrame;
    this.currentFrame.save();
  }

  getFunctionAnimationFrame(
    frame: AnimationFrame,
    relationships: AnimationFrameRelationships,
  ) {
    const frameObject = new FunctionAnimationFrameObject(frame, relationships);
    frameObject.init();
    return frameObject;
  }

  resolveScale(scale: Scale): _Scale {
    return {
      x: this.resolveNumber(scale?.x || 0),
      y: this.resolveNumber(scale?.y || 0),
      relative: scale?.relative as boolean,
    };
  }

  resolveNumber(variable: number | string) {
    return this.resolveLibaryValue<number>('number', variable);
  }

  resolveLibaryValue<T>(key: keyof Library, variable: any): T {
    if (typeof variable == 'string' && variable.startsWith('$')) {
      return this.currentProject.library?.[key]?.[variable.slice(2)] as T;
    }
    return variable;
  }
}
